import {Component, Input, OnChanges, SimpleChanges, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy} from "@angular/core";
import {Results, TreeAggregation, AggregationItem, TreeAggregationNode, Aggregation} from "@sinequa/core/web-services";
import {Utils} from "@sinequa/core/base";
import {FacetService} from "../../facet.service";
import {Action} from "@sinequa/components/action";
import {AbstractFacet} from "../../abstract-facet";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { Observable, of, Subscription, catchError, debounceTime, distinctUntilChanged, map, switchMap } from "rxjs";
import { FacetConfig } from "../../facet-config";

export interface FacetTreeParams {
    aggregation: string;
    showCheckbox?: boolean;
    showCount?: boolean;
    searchable?: boolean;
    allowExclude?: boolean;
    allowOr?: boolean;
    allowAnd?: boolean;
    expandedLevel?: number;
    forceMaxHeight?: boolean;
    displayActions?: boolean;
    alwaysShowSearch?: boolean;
    indentFactor?: number;
}

export interface FacetTreeConfig extends FacetConfig<FacetTreeParams> {
    type: 'tree';
}

@Component({
    selector: "sq-facet-tree",
    templateUrl: "./facet-tree.html",
    styleUrls: ["./facet-tree.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BsFacetTree extends AbstractFacet implements FacetTreeParams, OnChanges, OnDestroy {
    @Input() name: string; // If ommited, the aggregation name is used
    @Input() results: Results;
    @Input() aggregation: string;
    @Input() showCheckbox: boolean = true;
    @Input() showCount: boolean = true; // Show the number of occurrences
    @Input() allowExclude: boolean = true; // Allow to exclude selected items
    @Input() allowOr: boolean = true; // Allow to search various items in OR mode
    @Input() allowAnd: boolean = false; // Allow to search various items in OR mode
    @Input() searchable: boolean = true; // Allow to search for items in the facet
    @Input() expandedLevel: number = 2;
    @Input() forceMaxHeight: boolean = true; // Allow to display a scrollbar automatically on long list items
    @Input() displayActions = false;
    @Input() replaceCurrent = false; // if true, the previous "select" is removed first
    @Input() alwaysShowSearch = false;
    @Input() indentFactor = 0.5;

    // Aggregation from the Results object
    data: TreeAggregation | undefined;
    originalItems: AggregationItem[] | undefined;

    private readonly subscriptions: Subscription[] = [];

    // Sets to keep track of selected/excluded/filtered items
    readonly filtered = new Set<AggregationItem>();

    readonly selected = new Map<string,TreeAggregationNode>();

    hiddenSelected: TreeAggregationNode[] = [];
    // TODO keep track of excluded terms and display them with specific color private
    // readonly filtered = new Set<AggregationItem>();

    // Search
    myGroup: UntypedFormGroup;
    searchQuery: UntypedFormControl; // ngModel for textarea
    suggestDelay = 200;
    searchActive = false;
    noResults = false;

    // Actions (displayed in facet menu)
    // All actions are built in the constructor
    private readonly filterItemsOr: Action;
    private readonly filterItemsAnd: Action;
    private readonly excludeItems: Action;
    private readonly clearFilters: Action;
    public readonly searchItems: Action;


    constructor(
        private facetService: FacetService,
        private changeDetectorRef: ChangeDetectorRef){
            super();

            this.myGroup = new UntypedFormGroup({
                searchQuery: new UntypedFormControl()
            });

            this.searchQuery = this.myGroup.get("searchQuery") as UntypedFormControl;
            this.subscriptions["suggest"] = this.suggest$(this.searchQuery.valueChanges)
                .subscribe(values => {
                    if(this.data) {
                        const items = this.searchQuery.value? values : this.originalItems;
                        this.data = {
                            column: this.data.column,
                            name: this.data.name,
                            isTree: true,
                            items
                        }
                        // Refresh hiddenSelected list when the list of items is updated
                        this.refreshHiddenSelected();
                        this.searchActive = false;
                        this.changeDetectorRef.markForCheck();
                    }
                });

            // Keep documents with ANY of the selected items
            this.filterItemsOr = new Action({
                icon: "fas fa-filter",
                title: "msg#facet.filterItems",
                action: () => {
                    if (this.data) {
                        this.facetService.addFilterSearch(this.getName(), this.data, this.getSelectedItems());
                    }
                }
            });

            // Keep documents with ALL the selected items
            this.filterItemsAnd = new Action({
                icon: "fas fa-bullseye",
                title: "msg#facet.filterItemsAnd",
                action: () => {
                    if (this.data) {
                        this.facetService.addFilterSearch(this.getName(), this.data as Aggregation, this.getSelectedItems(), {and: true, replaceCurrent: this.replaceCurrent});
                }
            }
        });

            // Exclude document with selected items
            this.excludeItems = new Action({
                icon: "fas fa-times",
                title: "msg#facet.excludeItems",
                action: () => {
                    if (this.data) {
                        this.facetService.addFilterSearch(this.getName(), this.data, this.getSelectedItems(), {not: true});
                    }
                }
            });

            // Clear the current filters
            this.clearFilters = new Action({
                icon: "far fa-minus-square",
                title: "msg#facet.clearSelects",
                action: () => {
                    this.facetService.clearFiltersSearch(this.getName(), true);
                }
            });

            // Search for a value in this list
            this.searchItems = new Action({
                icon: "fas fa-search",
                title: "msg#facet.searchItems",
                action: (item, event) => {
                    item.selected = !item.selected;
                    if(!item.selected){
                        this.clearSearch();
                    }
                    event.stopPropagation();
                    this.changeDetectorRef.markForCheck();
                }
            });
    }

    /**
     * Name of the facet, used to create and retrieve selections
     * through the facet service.
     */
    getName() : string {
        return this.name || this.aggregation;
    }

    /**
     * OnChanges listener awaits new results from the search service
     * This completely resets the display
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges) {
        if (this.showCount === undefined) this.showCount = true;
        if (this.searchable === undefined) this.searchable = true;
        if (this.allowExclude === undefined) this.allowExclude = true;
        if (this.allowOr === undefined) this.allowOr = true;
        if (this.allowAnd === undefined) this.allowAnd = false;

        if (changes.results || changes.aggregation) {     // New data from the search service
            this.filtered.clear();
            this.selected.clear();
            this.hiddenSelected.length = 0;
            this.data = this.facetService.getAggregation(this.aggregation, this.results, {
                facetName: this.getName(),
                levelCallback: this.initNodes
            });
            this.originalItems = this.data?.items;
            this.searchItems.selected = !!this.alwaysShowSearch;
            this.clearSearch();
        }
    }

    // For each new node, set up properties necessary for display
    // This callback could also be used to filter or sorts nodes, etc.
    @Input()
    initNodes = (nodes: TreeAggregationNode[], level: number, node: TreeAggregationNode) => {
        if(node.$filtered){
            this.filtered.add(node);
        }
        if(node.hasChildren && !node.$opened && node.items && node.items.length >= 0 && level <= this.expandedLevel){
            node.$opened = true;
        }
    }

    /**
     * Returns all the actions that are relevant in the current context
     */
    override get actions(): Action[] {

        const actions: Action[] = [];

        if(this.selected.size > 0) {
            if(this.allowOr){
                actions.push(this.filterItemsOr);
            }
            if(this.allowAnd && (this.filtered.size + this.selected.size) > 1){
                actions.push(this.filterItemsAnd);
            }
            if(this.allowExclude){
                actions.push(this.excludeItems);
            }
        }

        if(this.hasFiltered()) {
            actions.push(this.clearFilters);
        }

        if(this.searchable && !this.alwaysShowSearch){
            actions.push(this.searchItems);
        }

        return actions;
    }


    // Filtered items

    /**
     * Returns true if the given AggregationItem is filtered
     * @param item
     */
    isFiltered(data: Aggregation | undefined, item: AggregationItem): boolean {
        const agg = Array.from(this.filtered);
        return this.facetService.filteredIndex(data, agg, item) !== -1
    }

    /**
     * Returns true if there is an active selection (or exclusion) from this facet
     */
    hasFiltered(): boolean {
        return this.facetService.hasFiltered(this.getName());
    }

    /**
     * Called when clicking on a facet item text
     * @param item
     * @param event
     */
    filterItem(item: AggregationItem, event) : boolean {
        if (this.data) {
            if(!this.isFiltered(this.data, item)) {
                this.facetService.addFilterSearch(this.getName(), this.data, item);
            }
            else {
                this.facetService.removeFilterSearch(this.getName(), this.data, item);
            }
        }
        event.preventDefault();
        event.stopPropagation();
        return false;   // Stop the propagation of the event (link inside link)
    }


    // Selected items

    /**
     * Returns true if the given AggregationItem is selected
     * @param item
     */
    isSelected(item: TreeAggregationNode) : boolean {
        return this.selected.has(item.$path!);
    }

    /**
     * Returns all the selected items
     */
    getSelectedItems(): TreeAggregationNode[] {
        return Array.from(this.selected.values());
    }

    /**
     * Called when selecting/unselecting an item in the facet
     * @param item
     */
    selectItem(item: TreeAggregationNode, event: Event): boolean {
        event.stopImmediatePropagation();
        if(!this.isFiltered(this.data, item)){
            if(this.selected.has(item.$path!)) {
                this.selected.delete(item.$path!);
            }
            else {
                this.selected.set(item.$path!, item);
            }
            this.refreshHiddenSelected();
        }
        else {
            this.filterItem(item, event);
        }
        return false;
    }

    refreshHiddenSelected() {
        this.hiddenSelected = this.getSelectedItems()
            .filter(item => !this.find(this.data?.items as TreeAggregationNode[], item));
    }

    find(items: TreeAggregationNode[] | undefined, item: TreeAggregationNode) {
        if(items) {
            for(const i of items) {
                if(i.$path === item.$path || (i.$opened && this.find(i.items, item))) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Expand/Collapse a Tree node (the data may need to downloaded from the server)
     * @param item
     */
    open(item: TreeAggregationNode, event: Event){
        if (item.hasChildren) {
            item.$opened = !item.$opened;
            if (!item.items || item.items.length === 0) {
                item['$opening'] = true;
                if (this.data) {
                    Utils.subscribe(this.facetService.open(this.getName(), this.data, item, this.initNodes),
                        (results) => {
                            item['$opening']= false;
                            this.refreshHiddenSelected();
                            this.changeDetectorRef.markForCheck();
                        });
                }
            }
            this.refreshHiddenSelected();
        }
        event.preventDefault();
        event.stopPropagation();
        return false; // Prevent default action
    }

    /* AbstractFacet abstract methods */
    override isHidden(): boolean {
        return !this.data;
    }


    // Search

    clearSearch() {
        this.searchQuery.setValue(""); // Remove suggestions if some remain
        this.noResults = false;
    }

    /**
     * Called on NgModel change (searchQuery)
     * Uses the suggestfield API to retrieve suggestions from the server
     * The suggestions "override" the data from the distribution (until search results are cleared)
     */
    suggest$ = (text$: Observable<string>) => text$.pipe(
        debounceTime(this.suggestDelay),
        distinctUntilChanged(),
        switchMap(term => {
            if (term.trim() === "") {
                this.noResults = false;
                return of([]);
            }
            this.changeDetectorRef.markForCheck();
            this.searchActive = true;
            return this.facetService.suggest(term, this.data?.column || '').pipe(
                catchError(err => {
                    console.log(err);
                    this.noResults = false;
                    return of([]);
                }),
                map(suggests => {
                    const items = this.facetService.suggestionsToTreeAggregationNodes(suggests, term, this.data);
                    this.noResults = items.length === 0 && term.trim() !== "";
                    return items;
                })
            )
        })
    )

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

}
